import React from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { FilterItemTooltipProps } from '@/components/Filter/types';
import { Filter } from '@/components/FilterManager';
import { NewFilterOption } from '@/components/FilterNew/types';

import { AddMoreButton } from '../../components/ActionButton';
import { getButtonLabel } from '../../SortAndFilter/helpers';
import { TagFilterProps } from './types';
import useTagsFilter from './useTagsFilter';
import { MENU_LIST_OVERRIDES } from './utils';

function TagsFilter(props: TagFilterProps) {
  const {
    open,
    onToggle,
    onClose,
    selectedValues,
    onChange,
    defaultPlaceholder,
    children,
    availableOn,
    tagType,
    showRequiredTags,
    EmptyState,
    isSingleSelect,
    isSharedItem,
    availableInLibrary,
    showProviders,
    fetchTagWithProviders,
    nonEditableFilterProperties,
    subWorkspaceIds,
  } = props;
  const { searchText, setSearchText, isFetching, filterGroups, error, fetchMore } = useTagsFilter({
    availableOn,
    requiredTag: showRequiredTags,
    isSharedItem,
    availableInLibrary,
    showProviders,
    fetchTagWithProviders,
    tagType,
    nonEditableFilterProperties,
    subWorkspaceIds,
  });
  const { t } = useTranslation(['Tags', 'Form']);
  const { isMobile } = useScreen();

  if (error) {
    return null;
  }

  if (EmptyState && searchText.length === 0 && filterGroups.length === 0 && !isFetching) {
    return EmptyState;
  }

  const getTooltipProps: FilterItemTooltipProps = ({ option }: { option: NewFilterOption }) => {
    return {
      title: option.label,
      variant: 'default',
      placement: 'right',
    };
  };

  return (
    <Filter
      asyncOptions={{
        fetching: isFetching,
        search: {
          appearance: isMobile ? 'filled' : undefined,
          shape: isMobile ? 'square' : undefined,
          size: isMobile ? 'medium' : undefined,
          placeholder: showProviders ? t('Common:Search') : t('Form.SearchTags'),
          value: searchText,
          onChange: setSearchText,
        },
        fetchMore,
      }}
      getTooltipProps={getTooltipProps}
      groups={filterGroups}
      isSingleSelect={isSingleSelect}
      open={open}
      overrides={MENU_LIST_OVERRIDES}
      showActionButtons={false}
      value={selectedValues}
      onChange={onChange}
      onClose={onClose}
      onToggle={onToggle}>
      {children || (
        <AddMoreButton isSelected={!!selectedValues.length}>
          {getButtonLabel(
            selectedValues.map((item) => item.label),
            defaultPlaceholder || t('Form.SearchTags'),
          )}
        </AddMoreButton>
      )}
    </Filter>
  );
}

export default React.memo(TagsFilter);
